import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import '../styles/componentStyles/DrinksList.css';

import { faChevronDown } from '@fortawesome/sharp-regular-svg-icons';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import NumberInput from '../components/NumberInput';

const data = [
  { label: 'T154', value: 'beer' },
  { label: 'T155', value: 'cider' },
  { label: 'T156', value: 'stout' },
  { label: 'T157', value: 'red_wine' },
  { label: 'T158', value: 'white_wine' },
  { label: 'T159', value: 'rose_wine' },
  { label: 'T160', value: 'sparkling_wine' },
  { label: 'T161', value: 'spirit_with_mixer' },
  { label: 'T162', value: 'spirit' },
];

const measureBeer = [
  { label: 'T540W', value: 'pints' },
  { label: 'T541W', value: 'half_pints' },
];

const measureWine = [
  { label: 'T542W', value: '125' },
  { label: 'T543W', value: '175' },
  { label: 'T544W', value: '250' },
];

const measureSpirits = [
  { label: 'T545W', value: 'single' },
  { label: 'T546W', value: 'double' },
];

const abvBeer = [3, 4, 5, 6, 7];
const abvWine = [11, 12, 13, 14, 15];
const abvSpirits = [20, 37.5, 40];

function DrinksList(props) {
  const { t } = useTranslation();
  const [lines, setLines] = useState([
    {
      id: 0,
      group: '',
      type: '',
      number: 0,
      size: '',
      strength: '',
    },
  ]);
  const inputRefs = useRef([]);
  const drinkTypeRefs = useRef({});
  const lastAddedLineId = useRef(null);

  useEffect(() => {
    // Prevent scrolling on number input
    const handleWheel = (event) => {
      event.preventDefault();
    };

    inputRefs.current.forEach((input) => {
      if (input) {
        input.addEventListener('wheel', handleWheel);
      }
    });

    return () => {
      inputRefs.current.forEach((input) => {
        if (input) {
          input.removeEventListener('wheel', handleWheel);
        }
      });
    };
  }, [lines]);

  useEffect(() => {
    if (props.data?.[props.questionid]?.length > 0) {
      const dataIntoLines = [];
      props.data[props.questionid].forEach((bev, index) => {
        const index2 = data.findIndex((el) => el.value === bev.type);
        let group;
        if (index2 < 3) {
          group = 'beer';
        } else if (index2 > 6) {
          group = 'spirits';
        } else {
          group = 'wine';
        }
        dataIntoLines.push({
          id: index,
          group,
          type: bev.type,
          number: bev.number,
          size: bev.size,
          strength: bev.strength,
        });
      });
      if (dataIntoLines.length > 0) {
        setLines(dataIntoLines);
      }
    }
  }, [props.data?.[props.questionid]]);

  useEffect(() => {
    // Focus on newly added alcohol item
    if (lastAddedLineId.current !== null && drinkTypeRefs.current[lastAddedLineId.current]) {
      drinkTypeRefs.current[lastAddedLineId.current].focus();
      lastAddedLineId.current = null;
    }
  }, [lines]);

  const handleDeleteLine = (id) => {
    setLines(lines.filter((line) => line.id !== id));
  };

  const handleAddLine = () => {
    const newLineId = lines.length > 0 ? lines[lines.length - 1].id + 1 : 0;
    lastAddedLineId.current = newLineId;

    setLines([
      ...lines,
      {
        id: newLineId,
        group: '',
        type: '',
        number: 0,
        size: '',
        strength: '',
      },
    ]);
  };

  const handleDrinkTypeChange = (event, lineId) => {
    const selectedValue = event.target.value;
    const index = data.findIndex((el) => el.value === selectedValue);
    let group;
    if (index < 3) {
      group = 'beer';
    } else if (index > 6) {
      group = 'spirits';
    } else {
      group = 'wine';
    }

    setLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id === lineId) {
          return {
            ...line,
            group,
            type: selectedValue,
            size: '',
            strength: '',
          };
        }
        return line;
      })
    );
  };

  const handleSizeChange = (event, lineId) => {
    const size = event.target.value;
    setLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id === lineId) {
          return {
            ...line,
            size,
          };
        }
        return line;
      })
    );
  };

  const handleStrengthChange = (event, lineId) => {
    const strength = event.target.value;
    setLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id === lineId) {
          return {
            ...line,
            strength,
          };
        }
        return line;
      })
    );
  };

  const handleNumberChange = (value, lineId) => {
    setLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id === lineId) {
          return { ...line, number: value };
        }
        return line;
      })
    );
  };

  const saveDrinks = () => {
    const drinksData = [];
    lines.forEach((line) => {
      if (line.number > 0 && line.type && line.size && line.strength) {
        drinksData.push({
          type: line.type,
          number: line.number,
          size: line.size,
          strength: line.strength,
        });
      }
    });
    if (drinksData.length > 0) {
      props.saveResponse('data', { id: props.questionid, value: drinksData });
    }
  };

  const getSizeOptions = (group) => {
    if (group === 'beer') return measureBeer;
    if (group === 'wine') return measureWine;
    if (group === 'spirits') return measureSpirits;
    return [];
  };

  const getStrengthOptions = (group) => {
    if (group === 'beer') return abvBeer;
    if (group === 'wine') return abvWine;
    if (group === 'spirits') return abvSpirits;
    return [];
  };

  return (
    <div
      role="group"
      aria-label={t('Your drinks')}
      onMouseLeave={saveDrinks}
      onTouchEnd={saveDrinks}
    >
      {lines.map((line) => (
        <div className={line.id} key={line.id}>
          <div className="buffer" />
          <div className="alc-line fs-mask">
            <div className="part-line">
              <div className="tracker-select-wrapper">
                <label htmlFor={`drink-type-${line.id}`} className="visuallyHidden">
                  {t('Select drink type')}
                </label>
                <select
                  id={`drink-type-${line.id}`}
                  className="drink-select"
                  value={line.type}
                  onChange={(e) => handleDrinkTypeChange(e, line.id)}
                  required
                  ref={(el) => {
                    drinkTypeRefs.current[line.id] = el;
                  }}
                >
                  <option value="" disabled>
                    {t('T153')}
                  </option>
                  {data.map((item) => (
                    <option key={item.value} value={item.value}>
                      {t(item.label)}
                    </option>
                  ))}
                </select>
                <FontAwesomeIcon icon={faChevronDown} className="select-icon" aria-hidden="true" />
              </div>

              <div className="bottom-row">
                <div className="column">
                  <NumberInput
                    type="inline"
                    inputName={`drink-number-${line.id}`}
                    startValue={line.number}
                    minValue={1}
                    maxValue={20}
                    onSetValue={(value) => handleNumberChange(value, line.id)}
                    controlsInfo={t('trackers.alcohol.unit')}
                    required={!!line.type}
                  />
                </div>

                <div className="column">
                  <div className="tracker-select-wrapper">
                    <label htmlFor={`drink-size-${line.id}`} className="visuallyHidden">
                      {t('Select drink size')}
                    </label>
                    <select
                      id={`drink-size-${line.id}`}
                      className="drink-select bev"
                      value={line.size}
                      onChange={(e) => handleSizeChange(e, line.id)}
                      disabled={!line.group}
                      required={!!line.type}
                    >
                      <option value="" disabled>
                        {t('T163')}
                      </option>
                      {getSizeOptions(line.group).map((item) => (
                        <option key={item.value} value={item.value}>
                          {t(item.label)}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="select-icon"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                <div className="column">
                  <div className="tracker-select-wrapper">
                    <label htmlFor={`drink-strength-${line.id}`} className="visuallyHidden">
                      {t('Select alcohol strength')}
                    </label>
                    <select
                      id={`drink-strength-${line.id}`}
                      className="drink-select bev"
                      value={line.strength}
                      onChange={(e) => handleStrengthChange(e, line.id)}
                      disabled={!line.group}
                      required={!!line.type}
                    >
                      <option value="" disabled>
                        %
                      </option>
                      {getStrengthOptions(line.group).map((item) => (
                        <option key={item} value={item}>
                          {t('T547W')} {item}%
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="select-icon"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="part-line">
              <button
                type="button"
                className="tracker-remove-item"
                onClick={() => handleDeleteLine(line.id)}
                aria-label={t('Remove drink')}
              >
                <FontAwesomeIcon icon={faTimes} className="icon times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="tracker-add-line"
        onClick={handleAddLine}
        aria-label={t('Add another drink')}
      >
        <div className="icon-cont">
          <FontAwesomeIcon icon={faPlus} className="icon plus" aria-hidden="true" />
        </div>
        <div>{t('Add another')}</div>
      </button>
    </div>
  );
}

export default DrinksList;
